import { 
  Box, 
  Button, 
  Card, 
  CardContent, 
  Chip, 
  Divider, 
  Grid, 
  Stack, 
  styled, 
  Typography,
  Avatar,
  Tooltip,
  Zoom
} from '@mui/material';
import { calendarEntriesApi } from 'api';
import { CalendarEntryEntity, CalendarEntryEntityTypeEnum } from 'api/generated';
import AppModal from 'components/AppModal';
import { H2, H4 } from 'components/Typography';
import { t } from 'i18next';
import { type FC, useState, useMemo } from 'react';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import PersonIcon from '@mui/icons-material/Person';
import EventIcon from '@mui/icons-material/Event';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ScheduleIcon from '@mui/icons-material/Schedule';
import DeleteIcon from '@mui/icons-material/Delete';

// component props interface
interface ModalProps {
  open: boolean;
  onClose: () => void;
  fetchData: () => void;
  requests: CalendarEntryEntity[]
}

// styled components
const StyledAppModal = styled(AppModal)(({ theme }) => ({
  maxWidth: 800,
  minWidth: 400,
  boxShadow: theme.shadows[24],
  [theme.breakpoints.down('sm')]: {
    maxWidth: '95vw',
    '& .main-form': { height: '80vh', overflow: 'auto' },
  },
}));

const EmployeeCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(1.5),
  border: `1px solid ${theme.palette.divider}`,
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    borderColor: theme.palette.primary.main,
    boxShadow: theme.shadows[4],
    transform: 'translateY(-1px)',
  },
}));

const RequestItem = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isAnimating' && prop !== 'animationDirection'
})<{ isAnimating: boolean; animationDirection: 'left' | 'right' | null }>(({ theme, isAnimating, animationDirection }) => ({
  padding: theme.spacing(1.5),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  marginBottom: theme.spacing(0.5),
  transition: isAnimating ? 'all 1.8s cubic-bezier(0.4, 0, 0.2, 1)' : 'all 0.2s ease-in-out',
  transform: 'none',
  opacity: 1,
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    borderColor: theme.palette.primary.light,
  },
}));

const AnimationOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  zIndex: 10,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  height: '100%',
  pointerEvents: 'none',
}));

const PendingRequestsModal: FC<ModalProps> = ({ open, onClose, fetchData, requests }) => {
  const [processing, setProcessing] = useState<Set<number>>(new Set());
  const [bulkProcessing, setBulkProcessing] = useState<Set<number>>(new Set());
  const [animatingRequests, setAnimatingRequests] = useState<Map<number, { direction: 'left' | 'right'; type: 'accept' | 'decline' }>>(new Map());

  // Group requests by employee
  const groupedRequests = useMemo(() => {
    const grouped = new Map<number, {
      employee: {
        id: number;
        firstName: string;
        lastName: string;
        filePath?: string | null;
      };
      requests: CalendarEntryEntity[];
    }>();

    requests.forEach(request => {
      const employeeId = request.employeeToSeason?.employee?.id || 0;
      const employee = request.employeeToSeason?.employee;
      
      if (!grouped.has(employeeId)) {
        grouped.set(employeeId, {
          employee: {
            id: employeeId,
            firstName: employee?.firstName || '',
            lastName: employee?.lastName || '',
            filePath: employee?.filePath,
          },
          requests: [],
        });
      }
      
      const group = grouped.get(employeeId);
      if (group) {
        group.requests.push(request);
      }
    });

    return Array.from(grouped.values());
  }, [requests]);

  const animateRequest = (requestId: number, direction: 'left' | 'right', type: 'accept' | 'decline') => {
    setAnimatingRequests(prev => new Map(prev).set(requestId, { direction, type }));
    
    // Remove the animation after it completes
    setTimeout(() => {
      setAnimatingRequests(prev => {
        const newMap = new Map(prev);
        newMap.delete(requestId);
        return newMap;
      });
    }, 2000);
  };

  const handleDelete = async (id: number, seasonId: number, employeeId: number) => {
    setProcessing(prev => new Set(prev).add(id));
    
    try {
      // Start animation immediately
      animateRequest(id, 'left', 'decline');
      
      await calendarEntriesApi.refuse(String(id), seasonId, employeeId);
      
      // Wait for icon to be visible, then start slide-out animation
      setTimeout(() => {
        // Start the slide-out animation
        const requestItem = document.querySelector(`[data-request-id="${id}"]`) as HTMLElement;
        if (requestItem) {
          requestItem.style.transform = 'translateX(-100%)';
          requestItem.style.opacity = '0';
        }
      }, 1200);
      
      // Wait for full animation to complete before fetching data
      setTimeout(() => {
        fetchData();
        
        if (requests.length <= 1) {
          onClose();
        }
      }, 2000);
      
    } catch (error) {
      console.error('Error refusing request:', error);
      // Remove animation if there's an error
      setAnimatingRequests(prev => {
        const newMap = new Map(prev);
        newMap.delete(id);
        return newMap;
      });
    } finally {
      setProcessing(prev => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      });
    }
  };

  const handleAccept = async (id: number, seasonId: number, employeeId: number) => {
    setProcessing(prev => new Set(prev).add(id));
    
    try {
      // Start animation immediately
      animateRequest(id, 'right', 'accept');
      
      await calendarEntriesApi.accept(String(id), seasonId, employeeId);
      
      // Wait for icon to be visible, then start slide-out animation
      setTimeout(() => {
        // Start the slide-out animation
        const requestItem = document.querySelector(`[data-request-id="${id}"]`) as HTMLElement;
        if (requestItem) {
          requestItem.style.transform = 'translateX(100%)';
          requestItem.style.opacity = '0';
        }
      }, 1200);
      
      // Wait for full animation to complete before fetching data
      setTimeout(() => {
        fetchData();
        
        if (requests.length <= 1) {
          onClose();
        }
      }, 2000);
      
    } catch (error) {
      console.error('Error accepting request:', error);
      // Remove animation if there's an error
      setAnimatingRequests(prev => {
        const newMap = new Map(prev);
        newMap.delete(id);
        return newMap;
      });
    } finally {
      setProcessing(prev => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      });
    }
  };

  const handleAcceptAllForEmployee = async (employeeId: number) => {
    const employeeRequests = groupedRequests.find(group => group.employee.id === employeeId);
    if (!employeeRequests) return;

    setBulkProcessing(prev => new Set(prev).add(employeeId));
    
    try {
      // Animate all requests for this employee
      employeeRequests.requests.forEach(request => {
        animateRequest(request.id, 'right', 'accept');
      });
      
      const promises = employeeRequests.requests.map(async request =>
        calendarEntriesApi.accept(String(request.id), request.seasonId, request.employeeId)
      );
      
      await Promise.all(promises);
      
      // Wait for animations to complete before fetching data
      setTimeout(() => {
        fetchData();
        
        if (requests.length <= employeeRequests.requests.length) {
          onClose();
        }
      }, 2000);
      
    } catch (error) {
      console.error('Error accepting all requests for employee:', error);
      // Remove all animations if there's an error
      setAnimatingRequests(new Map());
    } finally {
      setBulkProcessing(prev => {
        const newSet = new Set(prev);
        newSet.delete(employeeId);
        return newSet;
      });
    }
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  };

  const formatDateRange = (startDate: string, endDate: string) => {
    if (startDate === endDate) {
      return formatDate(startDate);
    }
    return `${formatDate(startDate)} - ${formatDate(endDate)}`;
  };

  const formatTime = (time: string) => {
    if (!time) return '';
    return time; // Time is already in HH:MM format
  };

  const getRequestTypeInfo = (request: CalendarEntryEntity) => {
    // Handle custom event types
    if (request.type === CalendarEntryEntityTypeEnum.Custom && request.customType) {
      return {
        label: request.customType.name,
        color: request.customType.colour || '#6c757d',
        isCustom: true
      };
    }
    
    // Handle built-in event types
    const typeColors: Record<string, 'primary' | 'secondary' | 'success' | 'warning' | 'error'> = {
      vacation: 'success',
      sick: 'error',
      personal: 'warning',
      training: 'primary',
      other: 'secondary',
      day_off: 'warning',
      half_day: 'primary',
      ill: 'error',
      unpaid_leave: 'secondary',
      work: 'primary',
      custom: 'secondary',
    };
    
    const color = typeColors[request.type.toLowerCase()] || 'secondary';
    const label = t(`employees.calendar.types.${request.type.toLowerCase()}`);
    
    return {
      label,
      color ,
      isCustom: false
    };
  };

  const isTimedEvent = (request: CalendarEntryEntity) => {
    return request.startTime && request.endTime;
  };

  if (requests.length === 0) {
    return (
      <StyledAppModal open={open} handleClose={onClose}>
        <Box textAlign="center" py={3}>
          <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main', mb: 1.5 }} />
          <H2 color="success.main" mb={1}>{t('employees.calendar.pendingRequests.empty.title')}</H2>
          <Typography variant="caption" color="text.secondary">
            {t('employees.calendar.pendingRequests.empty.subtitle')}
          </Typography>
        </Box>
      </StyledAppModal>
    );
  }

  return (
    <StyledAppModal open={open} handleClose={onClose}>
      <Box>
        {/* Header */}
        <Box mb={2} textAlign="center">
          <H2 mb={1}>{t('employees.calendar.pendingRequests.title')}</H2>
          <Typography variant="body2" color="text.secondary">
            {t('employees.calendar.pendingRequests.subtitle', {
              count: requests.length,
              plural: requests.length !== 1 ? 's' : '',
              employeeCount: groupedRequests.length,
              employeePlural: groupedRequests.length !== 1 ? 's' : ''
            })}
          </Typography>
        </Box>

        {/* Employee Groups */}
        <Box maxHeight="65vh" overflow="auto" pr={1}>
          {groupedRequests.map((group) => (
            <EmployeeCard key={group.employee.id} elevation={1}>
              <CardContent sx={{ py: 2, px: 2 }}>
                {/* Employee Header */}
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
                  <Box display="flex" alignItems="center" gap={1.5}>
                    <Avatar
                      src={group.employee.filePath || undefined}
                      sx={{ width: 40, height: 40, bgcolor: 'primary.main' }}
                    >
                      <PersonIcon />
                    </Avatar>
                    <Box>
                      <H4 mb={0.5} sx={{ fontSize: '1.1rem' }}>
                        {group.employee.firstName} {group.employee.lastName}
                      </H4>
                      <Typography variant="caption" color="text.secondary">
                        {t('employees.calendar.pendingRequests.employee.requests', {
                          count: group.requests.length,
                          plural: group.requests.length !== 1 ? 's' : ''
                        })}
                      </Typography>
                    </Box>
                  </Box>
                  
                  <Tooltip title={t('employees.calendar.pendingRequests.employee.acceptAllTooltip', {
                    count: group.requests.length,
                    firstName: group.employee.firstName
                  })}>
                    <Button
                      variant="contained"
                      color="success"
                      size="small"
                      startIcon={<CheckCircleIcon />}
                      onClick={async () => handleAcceptAllForEmployee(group.employee.id)}
                      disabled={bulkProcessing.has(group.employee.id)}
                      sx={{ minWidth: 120, height: 32 }}
                    >
                      {bulkProcessing.has(group.employee.id) 
                        ? t('employees.calendar.pendingRequests.employee.processing')
                        : t('employees.calendar.pendingRequests.employee.acceptAll')
                      }
                    </Button>
                  </Tooltip>
                </Box>

                <Divider sx={{ mb: 1.5 }} />

                {/* Individual Requests */}
                <Stack spacing={0.5}>
                  {group.requests.map((request) => {
                    const typeInfo = getRequestTypeInfo(request);
                    const hasTime = isTimedEvent(request);
                    const isAnimating = animatingRequests.has(request.id);
                    const animationData = animatingRequests.get(request.id);
                    
                    return (
                      <RequestItem 
                        key={request.id} 
                        isAnimating={isAnimating}
                        animationDirection={animationData?.direction || null}
                        sx={{ position: 'relative' }}
                        data-request-id={request.id}
                      >
                        {/* Animation Overlay */}
                        {isAnimating && (
                          <AnimationOverlay>
                            <Zoom in={isAnimating} timeout={200}>
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  width: 60,
                                  height: 60,
                                  borderRadius: '50%',
                                  backgroundColor: animationData?.type === 'accept' 
                                    ? 'success.main' 
                                    : 'error.main',
                                  color: 'white',
                                  boxShadow: 3,
                                }}
                              >
                                {animationData?.type === 'accept' ? (
                                  <CheckCircleIcon sx={{ fontSize: 32 }} />
                                ) : (
                                  <DeleteIcon sx={{ fontSize: 32 }} />
                                )}
                              </Box>
                            </Zoom>
                          </AnimationOverlay>
                        )}

                        <Grid container alignItems="center" spacing={1.5}>
                          <Grid item xs={12} md={8}>
                            <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                              <EventIcon color="action" fontSize="small" />
                              <Chip
                                label={typeInfo.label}
                                color={typeInfo.isCustom ? undefined : (typeInfo.color as 'primary' | 'secondary' | 'success' | 'warning' | 'error')}
                                sx={{
                                  backgroundColor: typeInfo.isCustom ? typeInfo.color : undefined,
                                  color: typeInfo.isCustom ? 'white' : undefined,
                                  '& .MuiChip-label': {
                                    color: typeInfo.isCustom ? 'white' : undefined,
                                  }
                                }}
                                size="small"
                                variant="outlined"
                              />
                            </Box>
                            
                            <Box display="flex" alignItems="center" gap={1}>
                              <AccessTimeIcon color="action" fontSize="small" />
                              <Typography variant="caption" color="text.secondary">
                                {formatDateRange(request.startedOn, request.endedOn)}
                              </Typography>
                            </Box>

                            {/* Show time information if it's a timed event */}
                            {hasTime && request.startTime && request.endTime && (
                              <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                                <ScheduleIcon color="action" fontSize="small" />
                                <Typography variant="caption" color="text.secondary">
                                  {formatTime(request.startTime)} - {formatTime(request.endTime)}
                                </Typography>
                              </Box>
                            )}
                          </Grid>

                          <Grid item xs={12} md={4}>
                            <Stack direction="row" spacing={1} justifyContent={{ xs: 'stretch', md: 'flex-end' }}>
                              <Button
                                variant="contained"
                                color="success"
                                size="small"
                                startIcon={<CheckCircleIcon />}
                                onClick={async () => handleAccept(request.id, request.seasonId, request.employeeId)}
                                disabled={processing.has(request.id) || isAnimating}
                                fullWidth={false}
                                sx={{ minWidth: 70, height: 28, fontSize: '0.75rem' }}
                              >
                                {processing.has(request.id) 
                                  ? t('employees.calendar.pendingRequests.actions.processing')
                                  : t('employees.calendar.pendingRequests.actions.accept')
                                }
                              </Button>
                              <Button
                                variant="outlined"
                                color="error"
                                size="small"
                                startIcon={<CancelIcon />}
                                onClick={async () => handleDelete(request.id, request.seasonId, request.employeeId)}
                                disabled={processing.has(request.id) || isAnimating}
                                fullWidth={false}
                                sx={{ minWidth: 70, height: 28, fontSize: '0.75rem' }}
                              >
                                {processing.has(request.id) 
                                  ? t('employees.calendar.pendingRequests.actions.processing')
                                  : t('employees.calendar.pendingRequests.actions.decline')
                                }
                              </Button>
                            </Stack>
                          </Grid>
                        </Grid>
                      </RequestItem>
                    );
                  })}
                </Stack>
              </CardContent>
            </EmployeeCard>
          ))}
        </Box>

        {/* Footer */}
        <Box mt={2} textAlign="center">
          <Typography variant="caption" color="text.secondary">
            {t('employees.calendar.pendingRequests.footer')}
          </Typography>
        </Box>
      </Box>
    </StyledAppModal>
  );
};

export default PendingRequestsModal;
